import { forwardRef, useCallback, useEffect, useEffectEvent, useId, useState } from "react";

import { TooltipProps, TooltipReferenceProps } from "./types";
import TooltipFloating from "./Floating";

const CLOSE_DELAY = 100;

/**
 * Rendering hundreds of tooltips (e.g. a large table) used to be expensive because
 * every instance eagerly set up the full floating-ui hook tree (positioning, hover,
 * focus, dismiss, role, transitions) on mount and tore it all down on unmount.
 *
 * This implementation keeps the trigger lightweight: the wrapper always renders the
 * trigger (so its DOM node is never recreated and clicks/refs behave normally) and
 * drives open/close with a few native listeners. The heavy positioning layer
 * (`TooltipFloating`) is only mounted while the tooltip is actually open, so tooltips
 * that are never hovered cost almost nothing to mount or unmount.
 *
 * Prefer the smallest `widthMode` that fits: pick a preset for known copy; leave the
 * default (`maxWidthXl`) for long or dynamic content.
 */
const Tooltip = forwardRef(function Tooltip(props: TooltipProps, ref: React.ForwardedRef<Element>) {
  const {
    on,
    children,
    placement = "top",
    widthMode = "maxWidthXl",
    active: propsActive,
    forceOpen,
    onChildrenChangedCallback,
    delay = 300,
    mode = "portal",
    textAlign = "left",
    triggerClassName,
    disableAriaLabel,
    offset: tooltipOffset = 8,
  } = props;

  const active = "active" in props ? propsActive : true;

  const tooltipId = useId();

  const [node, setNode] = useState<HTMLElement | null>(null);
  const [userOpen, setUserOpen] = useState(false);
  // Whether the floating layer is rendered. Kept mounted during the exit transition.
  const [mounted, setMounted] = useState(Boolean(forceOpen));

  const open = forceOpen ? true : Boolean(active) && userOpen;

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      setNode(element);
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref]
  );

  const notifyChildrenChanged = useEffectEvent((currentNode: HTMLElement | null) => {
    onChildrenChangedCallback?.({ current: currentNode });
  });

  useEffect(() => {
    notifyChildrenChanged(node);
  }, [children, node]);

  // Mount the floating layer as soon as the tooltip should be open. It unmounts
  // itself (via `onExited`) once the exit transition has finished.
  useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  const handleExited = useCallback(() => {
    setMounted(false);
  }, []);

  // Link the trigger to the tooltip for assistive technologies while it is open.
  useEffect(() => {
    if (!node || disableAriaLabel || !open) {
      return;
    }
    node.setAttribute("aria-describedby", tooltipId);
    return () => node.removeAttribute("aria-describedby");
  }, [node, disableAriaLabel, open, tooltipId]);

  // Drive open/close natively so we never spin up floating-ui interaction hooks per
  // trigger. Listeners are bound directly to the reference element to mirror how
  // floating-ui's `useHover`/`useFocus` behave (e.g. native `mouseenter`).
  useEffect(() => {
    if (!node || !active || forceOpen) {
      return;
    }

    let openTimer: number | undefined;
    let closeTimer: number | undefined;

    const clearTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };

    const scheduleOpen = () => {
      clearTimers();
      openTimer = window.setTimeout(() => setUserOpen(true), delay);
    };

    const scheduleClose = () => {
      clearTimers();
      closeTimer = window.setTimeout(() => setUserOpen(false), CLOSE_DELAY);
    };

    const openNow = () => {
      clearTimers();
      setUserOpen(true);
    };

    const closeNow = () => {
      clearTimers();
      setUserOpen(false);
    };

    const handleFocus = () => {
      // Mirror floating-ui's `useFocus({ visibleOnly: true })`: only open on keyboard
      // focus, not on pointer-driven focus (e.g. clicking a button).
      try {
        if (!node.matches(":focus-visible")) {
          return;
        }
      } catch {
        // Older browsers may throw on `:focus-visible`; fall through and open.
      }
      openNow();
    };

    // Bound to the trigger, so Escape only dismisses a focused (keyboard-opened)
    // tooltip. A purely hover-opened tooltip closes on mouse-leave instead.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeNow();
      }
    };

    node.addEventListener("mouseenter", scheduleOpen);
    node.addEventListener("mouseleave", scheduleClose);
    node.addEventListener("focusin", handleFocus);
    node.addEventListener("focusout", closeNow);
    node.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimers();
      node.removeEventListener("mouseenter", scheduleOpen);
      node.removeEventListener("mouseleave", scheduleClose);
      node.removeEventListener("focusin", handleFocus);
      node.removeEventListener("focusout", closeNow);
      node.removeEventListener("keydown", handleKeyDown);
    };
  }, [node, active, forceOpen, delay]);

  // If the tooltip stops being active while open, reset so it doesn't reopen later.
  useEffect(() => {
    if (!active && userOpen) {
      setUserOpen(false);
    }
  }, [active, userOpen]);

  const triggerProps: TooltipReferenceProps = {
    ref: setRef,
  };

  if (triggerClassName) {
    triggerProps.className = triggerClassName;
  }

  return (
    <>
      {on(triggerProps)}
      {mounted && node && (
        <TooltipFloating
          reference={node}
          open={open}
          tooltipId={tooltipId}
          placement={placement}
          widthMode={widthMode}
          textAlign={textAlign}
          tooltipOffset={tooltipOffset}
          mode={mode}
          onExited={handleExited}
        >
          {children}
        </TooltipFloating>
      )}
    </>
  );
});

Tooltip.displayName = "DS.Tooltip";

export default Tooltip;
